/* SEO Articles — Batch 2 (Feb 2026).
 *
 * Targeted at the highest-volume distressed-caregiver searches that the
 * original 45-article library wasn't covering yet (UTI confusion, exit
 * seeking, paranoia, ER visits, etc.). Same shape as ARTICLES in
 * seoArticles.js; concatenated into ARTICLES on import.
 *
 * Every article auto-flows into: sitemap.xml, llms.txt, the related-
 * article knowledge graph, ArticlePage canonical URL + schema, and the
 * Universal Search index. No per-article wiring required.
 */
export const ARTICLES_BATCH_2 = [
    // ===== LEARN =====
    {
        slug: "early-onset-alzheimers",
        group: "learn",
        title: "Early-Onset Alzheimer's — Diagnosis Before 65",
        meta: "Early-onset Alzheimer's hits people under 65. What the diagnosis means for work, insurance, kids, and how to plan the next 12 months.",
        h1: "Early-onset Alzheimer's",
        summary: "About 1 in 20 people with Alzheimer's were diagnosed before 65. The disease behaves similarly, but the social and financial impact is very different.",
        body: [
            { type: "h2", text: "What 'early onset' actually means" },
            { type: "p", text: "Symptoms start before age 65. Most cases are sporadic (no clear family pattern). A small minority are caused by inherited gene mutations (PSEN1, PSEN2, APP) and tend to start in the 40s or 50s." },
            { type: "h2", text: "Why it feels different" },
            { type: "ul", items: [
                "You may still be working. Disability paperwork (SSDI under the Compassionate Allowances list) should start immediately.",
                "You may still have kids at home. Plan how and when to tell them — there are excellent age-appropriate books on the topic.",
                "Long-term care insurance and Medicaid look very different before age 65.",
                "Clinical trial eligibility is often broader for younger patients — ClinicalTrials.gov is worth a careful look.",
            ]},
            { type: "h2", text: "What to do in the first 90 days" },
            { type: "ol", items: [
                "Confirm the diagnosis at a memory clinic — pseudobulbar conditions and depression mimic early Alzheimer's.",
                "File for SSDI immediately — early-onset Alzheimer's qualifies for the Compassionate Allowances list and processes in weeks.",
                "Talk to an elder-law attorney about powers of attorney, trusts, and protecting assets before Medicaid look-back.",
                "Check eligibility for the Medicare GUIDE program once Medicare kicks in (typically 24 months after SSDI starts).",
            ]},
        ],
        faq: [
            { q: "Is early-onset Alzheimer's inherited?", a: "Most cases are not. Only a small percentage (<5%) involve inherited gene mutations. Genetic counseling is appropriate if you have a strong family history at young ages." },
            { q: "Does early-onset progress faster?", a: "On average, yes — but ranges widely. Younger brains tolerate decline less well partly because expectations are higher (work, parenting)." },
        ],
        related: ["what-is-dementia", "stages-of-dementia", "power-of-attorney", "ltc-insurance", "guide-eligibility"],
        lane_prompt: "My loved one was just diagnosed with early-onset Alzheimer's. What do we do first?",
        updated_at: "2026-02-27",
    },
    {
        slug: "dementia-and-uti",
        group: "learn",
        title: "UTI Confusion in Dementia — When 'Worse Overnight' Is Treatable",
        meta: "A urinary tract infection can cause sudden, severe confusion in someone with dementia. How to recognize it, what to ask the doctor for, and what to do today.",
        h1: "UTIs in dementia: the overnight crash you can fix",
        summary: "If your loved one with dementia became dramatically more confused, agitated, or sleepy in days — not months — please rule out a UTI before anyone blames the dementia.",
        body: [
            { type: "callout", tone: "coral", text: "Sudden behavior change in 24–72 hours = think delirium first. UTIs are the single most common cause in older adults." },
            { type: "h2", text: "What a dementia-plus-UTI presentation looks like" },
            { type: "ul", items: [
                "Increased confusion, hallucinations, paranoia, or sleepiness — without the classic burning or frequency.",
                "Sudden incontinence or fishy-smelling urine.",
                "New falls or unsteady walking.",
                "Refusing to eat or drink.",
                "Fever may be ABSENT in older adults — don't wait for one.",
            ]},
            { type: "h2", text: "What to do today" },
            { type: "ol", items: [
                "Call the primary care doctor and ask specifically for a urine dip and culture — say 'sudden confusion, please rule out UTI.'",
                "If they can't see your loved one within 24 hours, an urgent-care visit is reasonable. ER if there's fever, severe lethargy, or chest pain.",
                "Push fluids — water, broth, diluted juice — while waiting for results.",
                "Hold any sedating medications (benzos, sleep aids) if you can do so safely and ask the doctor whether anticholinergics should pause too.",
            ]},
            { type: "h2", text: "Preventing the next one" },
            { type: "ul", items: [
                "Cranberry capsules have weak but real evidence — generally safe to try.",
                "Toilet schedule every 2–3 hours, especially after meals.",
                "Front-to-back wiping; check that personal-care aides are doing this.",
                "Hydration: 6+ glasses daily. Track on the fridge if needed.",
            ]},
        ],
        faq: [
            { q: "Why do UTIs cause confusion in dementia?", a: "Any infection can trigger delirium in a vulnerable brain. UTIs are easy to miss because the classic symptoms (burning, urgency) are often absent in older adults." },
            { q: "How long until they 'come back' after antibiotics?", a: "24–72 hours is typical, but baseline cognition may take 4–6 weeks to fully return. If they're not improving after 5 days, retest the urine." },
        ],
        related: ["sundowning", "hallucinations", "agitation", "sleep-disturbance"],
        lane_prompt: "Mom got suddenly more confused this week. Could it be a UTI?",
        updated_at: "2026-02-27",
    },
    {
        slug: "dementia-and-eating-difficulties",
        group: "learn",
        title: "When Eating Gets Hard — Refusal, Spitting, Swallowing Trouble",
        meta: "Why people with dementia stop eating, when to worry, and the small adjustments that keep mealtimes calm and safe.",
        h1: "Eating, refusing food, and swallowing safely",
        summary: "Late-stage dementia almost always changes how a person eats. The goal shifts from nutrition to comfort — and small changes make a huge difference.",
        body: [
            { type: "h2", text: "Why eating changes" },
            { type: "ul", items: [
                "Taste and smell fade — sweet flavors stay last, salt fades early.",
                "Recognition: they may forget what a fork is for. Try finger foods.",
                "Dysphagia (swallowing trouble) — coughing while drinking is the earliest sign.",
                "Depression and medication side effects can both blunt appetite.",
            ]},
            { type: "h2", text: "Make mealtimes work" },
            { type: "ul", items: [
                "Plate with high color contrast — red plate, white food.",
                "One food at a time. A 'crowded plate' overwhelms.",
                "Hand-over-hand: place the spoon in their hand and guide it up.",
                "Sweet and savory — many late-stage patients gravitate to ice cream, smoothies, peanut butter.",
                "Eat together. Mirroring helps cue the action.",
            ]},
            { type: "h2", text: "When to call a speech-language pathologist (SLP)" },
            { type: "ul", items: [
                "Frequent coughing or wet-sounding voice after drinks.",
                "Holds food in cheek without swallowing.",
                "Recurrent 'pneumonia' — could be aspiration pneumonia.",
                "Medicare covers SLP evaluation — ask the doctor for a referral.",
            ]},
            { type: "callout", tone: "teal", text: "Late-stage refusal to eat may be a sign the body is starting to shut down. It's a sign for a hospice conversation — not for a feeding tube." },
        ],
        faq: [
            { q: "Should we get a feeding tube?", a: "For most people with advanced dementia, feeding tubes do NOT prolong life, do NOT prevent pneumonia, and worsen quality of life. The American Geriatrics Society recommends against them." },
            { q: "What if they only want sweets?", a: "Let them. Comfort and joy at meals outweigh balanced nutrition at this stage." },
        ],
        related: ["refusing-care", "hospice-benefit", "agitation", "sundowning"],
        lane_prompt: "Dad has stopped eating well. What should we do?",
        updated_at: "2026-02-27",
    },
    {
        slug: "caregiver-burnout-signs",
        group: "learn",
        title: "Caregiver Burnout — The Signs You're Not Allowed to Ignore",
        meta: "Burnout is the #1 reason dementia caregivers can't keep going. Here's how to spot it in yourself, and what actually helps.",
        h1: "Caregiver burnout: the warning signs",
        summary: "Burnout is not a feeling — it's a physiological state. Catching it early lets you act before you (or your loved one) gets hurt.",
        body: [
            { type: "h2", text: "The signs" },
            { type: "ul", items: [
                "Persistent exhaustion that sleep doesn't fix.",
                "Resentment or rage that surprises you.",
                "Crying without an obvious trigger.",
                "Withdrawing from friends, faith community, your own hobbies.",
                "Drinking more, smoking again, eating more or much less.",
                "New back pain, blood-pressure spikes, frequent colds — the body keeps score.",
            ]},
            { type: "h2", text: "Why caregivers burn out faster than anyone else" },
            { type: "p", text: "Dementia caregiving is 24/7, unpredictable, often grief-saturated, and rarely paid. National surveys show dementia caregivers are 2x more likely to be depressed, 30% more likely to die in any given year than non-caregivers." },
            { type: "h2", text: "What actually helps (in order of impact)" },
            { type: "ol", items: [
                "Respite. 2 hours, weekly. Non-negotiable. Look at GUIDE respite ($2,500/year free) or adult day programs.",
                "A therapist who specializes in caregiver stress — sliding-scale options exist via local Alzheimer's Association.",
                "Sleep. Most caregivers chronically under-sleep. A baby monitor + door alarm beats lying awake listening.",
                "One real friend you can call without explaining. Doesn't have to be many people.",
                "Movement — 20 min a day. Walking, stretching, anything. Builds resilience your body has lost.",
            ]},
        ],
        faq: [
            { q: "Is wanting them to die a bad sign?", a: "It's a common, normal sign that you've passed your limit. Tell a doctor or therapist today. It does not make you a bad person." },
            { q: "How do I take time off when no one else will help?", a: "Paid respite is real. The Medicare GUIDE program covers up to $2,500/year. Your loved one's hospice benefit covers 5-day inpatient respite. Local Alzheimer's chapters often have emergency funds." },
        ],
        related: ["finding-respite", "guide-eligibility", "anticipatory-grief", "sharing-the-diagnosis"],
        lane_prompt: "I think I'm burning out. What are the signs?",
        updated_at: "2026-02-27",
    },
    {
        slug: "anticipatory-grief",
        group: "learn",
        title: "Anticipatory Grief — Mourning Someone Who Is Still Here",
        meta: "Why caregivers grieve before the person dies, and how to live alongside it without burning out.",
        h1: "Anticipatory grief",
        summary: "You're losing someone in pieces — a memory here, a personality trait there. That grief is real, valid, and unavoidable. It's also survivable.",
        body: [
            { type: "h2", text: "What anticipatory grief looks like" },
            { type: "ul", items: [
                "Crying at the version of them you remember from last year.",
                "Guilt about wanting it to be 'over.'",
                "Numbness — feeling nothing when you think you should feel something.",
                "Anger at the disease, at family, at the universe.",
                "Anxiety about what comes next, even when nothing is happening.",
            ]},
            { type: "h2", text: "Why it gets harder, not easier" },
            { type: "p", text: "Anticipatory grief isn't linear. It often gets harder in middle-stage — when the person is still 'them' just enough that the losses sting sharply." },
            { type: "h2", text: "What helps" },
            { type: "ul", items: [
                "Name it. Telling someone 'I'm grieving' makes it survivable. It's not depression, but they overlap.",
                "Memory-keeping — record voice memos of stories, save handwriting.",
                "Permission to feel relief alongside sadness. Both are true.",
                "Caregiver-grief support groups — many are free through hospice agencies even before hospice is needed.",
            ]},
        ],
        faq: [
            { q: "Is it normal to feel guilty for grieving?", a: "Yes. You haven't 'lost' them yet by some definitions, so caregivers often feel grief shouldn't apply. It does. Honor it." },
            { q: "Will grief be easier when they die?", a: "Often the immediate weeks are calmer because the day-to-day strain stops. But fresh waves of grief can come months later. Plan for support." },
        ],
        related: ["caregiver-burnout-signs", "hospice-benefit", "talking-with-siblings", "end-of-life-wishes"],
        lane_prompt: "I'm grieving mom even though she's still alive. Is this normal?",
        updated_at: "2026-02-27",
    },

    // ===== BEHAVIOR =====
    {
        slug: "paranoia-and-accusations",
        group: "behavior",
        title: "Paranoia and Accusations — 'You're Stealing From Me'",
        meta: "Why people with dementia accuse caregivers of stealing, and how to respond without breaking the relationship.",
        h1: "Paranoia and accusations",
        summary: "Accusations of stealing, infidelity, or impersonation are the brain trying to fill in gaps. They're symptoms, not insults — but they hurt anyway.",
        body: [
            { type: "h2", text: "Why this happens" },
            { type: "p", text: "When the brain can't find an item or a memory, it builds an explanation. 'Someone took it' is faster than 'I don't remember where I put it.' The accusation isn't about you — but you're the closest person, so it lands on you." },
            { type: "h2", text: "What works in the moment" },
            { type: "ul", items: [
                "Don't defend yourself. Logic doesn't reach a brain in crisis.",
                "Validate the feeling: 'That must feel awful. Let's look together.'",
                "Help them 'find' it. Have a duplicate ready (wallet, glasses, keys, a favorite necklace).",
                "Shift the focus. After 5–10 minutes of looking, suggest tea or a walk.",
            ]},
            { type: "h2", text: "Prevent the next one" },
            { type: "ul", items: [
                "Designate one 'safe spot' for valuables — and check it weekly.",
                "Photograph important items.",
                "Note when accusations spike — sundowning hours, after visitors, when you've been away.",
                "Tell trusted family this is a symptom. Otherwise, accusations like 'my son is stealing' can poison relationships.",
            ]},
            { type: "callout", tone: "coral", text: "Accusations of infidelity directed at a spouse are particularly painful. Naming it as a brain symptom — and finding a therapist who understands dementia — helps." },
        ],
        faq: [
            { q: "Will it get worse?", a: "Often paranoia peaks in middle stage and fades in late stage as the brain has fewer resources for elaborate explanations. Severe paranoia may respond to low-dose antipsychotics — discuss with the doctor." },
            { q: "Should we move the person who's being accused?", a: "Usually no. The accusations follow the disease, not the caregiver. Moving rarely fixes it and often makes everything worse." },
        ],
        related: ["hallucinations", "agitation", "aggression", "sundowning"],
        lane_prompt: "Mom keeps accusing me of stealing from her. How do I respond?",
        updated_at: "2026-02-27",
    },
    {
        slug: "shadowing",
        group: "behavior",
        title: "Shadowing — When They Won't Let You Out of Sight",
        meta: "Shadowing is the dementia behavior where your loved one follows you everywhere. Why it happens and 6 things that help.",
        h1: "Shadowing",
        summary: "Shadowing means they follow you room to room, panic when you're out of sight, and ask the same question every two minutes. It's anxiety in disguise — and you can work with it.",
        body: [
            { type: "h2", text: "What's underneath the shadowing" },
            { type: "p", text: "You are their compass. When short-term memory dissolves, you become the one fixed point — and being away from you, even for 90 seconds, feels like being lost." },
            { type: "h2", text: "What helps" },
            { type: "ul", items: [
                "Narrate. 'I'm going to the bathroom. Back in 2 minutes.' Repeat as often as needed.",
                "An anchor object — a photo of you taped to a chair, a worn hoodie of yours nearby, audio of your voice on a loop.",
                "Familiar TV or a favorite playlist on while you step away.",
                "Adult day programs build tolerance for being apart in a safe setting.",
                "Schedule predictable 'together' times so they don't feel chased.",
                "Find a substitute attachment figure — a regular visitor, a paid companion, a grandchild.",
            ]},
            { type: "callout", tone: "teal", text: "Shadowing is exhausting because there's no privacy. Two hours of respite a week — even just so you can shower in peace — protects your ability to keep caregiving." },
        ],
        faq: [
            { q: "Why does it spike in the late afternoon?", a: "Sundowning overlaps with shadowing. The brain fatigues, anxiety rises, and the safest person becomes the only anchor." },
            { q: "Will it pass?", a: "Most caregivers report shadowing peaks in middle stage and decreases as mobility declines in late stage. Until then, structure helps more than reassurance alone." },
        ],
        related: ["sundowning", "agitation", "finding-respite", "caregiver-burnout-signs"],
        lane_prompt: "Mom follows me everywhere. How do I get a moment to myself?",
        updated_at: "2026-02-27",
    },
    {
        slug: "exit-seeking",
        group: "behavior",
        title: "'I Want to Go Home' — Exit Seeking in Dementia",
        meta: "Why people with dementia say they want to go home even when they ARE home, and how to respond.",
        h1: "Exit seeking and 'I want to go home'",
        summary: "'Home' usually doesn't mean the building. It means safety, the past, or a moment they remember being okay. Arguing about the address never works.",
        body: [
            { type: "h2", text: "Decode the request" },
            { type: "p", text: "'I want to go home' often means one of: I'm scared, I'm bored, I'm in pain, I need the bathroom, I want my mother, I want it to be the way it was. The brain converts feelings into actions it remembers — and 'go home' is a familiar plan." },
            { type: "h2", text: "What works" },
            { type: "ul", items: [
                "Don't correct ('you ARE home'). Acknowledge the feeling: 'You really miss home, don't you?'",
                "Redirect with a memory: 'Tell me about your mama. What did she cook?'",
                "Walk together. A 15-minute walk often resets the request.",
                "Check for pain, hunger, bathroom needs, sundowning timing — the 'home' request is often a proxy.",
                "Have a 'coming home' script ready: 'It's getting dark. Let's stay here tonight and head home in the morning.' By morning the impulse has usually passed.",
            ]},
            { type: "h2", text: "Safety: when exit seeking becomes wandering" },
            { type: "ul", items: [
                "Door alarms (~$15) on every external door.",
                "Hidden door locks placed above eye level.",
                "GPS tracker on a watch or in a shoe insert.",
                "Enroll in the local Wandering Registry / Project Lifesaver.",
            ]},
        ],
        faq: [
            { q: "What if they get out and we can't find them?", a: "Call 911 first, then send a photo to the Silver Alert system. Most wanderers are found within a mile of home and often near water — search those first." },
            { q: "Will moving to memory care make this worse?", a: "Initially, often yes — for 2–4 weeks. After that, most residents settle and 'home' fades. Talk to the memory-care team about a tailored transition plan." },
        ],
        related: ["wandering", "sundowning", "moving-to-memory-care", "agitation"],
        lane_prompt: "Dad keeps asking to go home even though he IS home. What do I say?",
        updated_at: "2026-02-27",
    },
    {
        slug: "resistance-to-medication",
        group: "behavior",
        title: "Refusing to Take Pills — 6 Approaches That Work",
        meta: "When someone with dementia refuses medication, what to try, what to skip, and when to call the pharmacist.",
        h1: "Refusing medication",
        summary: "Skipping medications is dangerous, but forcing them is worse. Almost every med has an alternative — start with the pharmacist.",
        body: [
            { type: "h2", text: "Start by asking why" },
            { type: "ul", items: [
                "Pills are hard to swallow — ask about liquid or sublingual forms.",
                "They taste bad — gel caps, chocolate pudding, applesauce vehicles.",
                "Paranoia ('you're poisoning me') — let a different family member or aide give it.",
                "Refusing to be sick — frame it as a vitamin or a heart-strengthener.",
                "Side effects they can't articulate — bring up at next doctor visit.",
            ]},
            { type: "h2", text: "6 approaches" },
            { type: "ol", items: [
                "Crush + mix with applesauce, pudding, or yogurt (check with pharmacist — not all pills crush safely).",
                "Switch to liquid or dissolvable form — most common meds have these.",
                "Use a pill box with hidden compartments OR a vitamin organizer they pour themselves.",
                "Time it with a beloved ritual — coffee, soap opera, after-lunch chocolate.",
                "Patches: cholinesterase inhibitors and many other meds come as transdermal patches.",
                "Re-evaluate the med list. With your pharmacist or doctor, drop any that aren't doing meaningful work.",
            ]},
            { type: "callout", tone: "teal", text: "Never hide medications in food for a person who can't legally consent without first discussing it with the doctor. There's a legal and ethical line. Most US states allow it when the family caregiver acts in the person's best interest and standard methods have failed." },
        ],
        faq: [
            { q: "Should we give them with extra force if they refuse?", a: "Almost never. Physical resistance + medication is a recipe for aspiration and traumatic incidents. Try a different time, person, or form first." },
            { q: "What if they refuse a critical med (insulin, blood thinner)?", a: "Call the doctor today. They can usually find an alternative or simplify the regimen. Refusing care is itself a clinical event." },
        ],
        related: ["refusing-care", "refusing-bath", "agitation", "telehealth-options"],
        lane_prompt: "Mom is refusing her medications. What should I try?",
        updated_at: "2026-02-27",
    },
    {
        slug: "catastrophic-reactions",
        group: "behavior",
        title: "Catastrophic Reactions — When a Small Thing Triggers an Explosion",
        meta: "Why someone with dementia melts down over a sock and what to do during and after the storm.",
        h1: "Catastrophic reactions",
        summary: "A catastrophic reaction is an outsized emotional response — yelling, crying, even striking — triggered by something that seems trivial. It's the brain hitting overload.",
        body: [
            { type: "h2", text: "What's happening in the brain" },
            { type: "p", text: "The 'thinking' part has run out of capacity. Sensory input, decision-making, language — it all hits at once. The reaction is the system tripping its breaker." },
            { type: "h2", text: "In the moment" },
            { type: "ul", items: [
                "Lower your voice. Slow your movements. Take a step back.",
                "Don't reason or argue. Cognitive resources are gone.",
                "Reduce stimulation: lower lights, turn off the TV, ask others to leave the room.",
                "Offer one simple choice: 'water or juice?'",
                "Wait. Most catastrophic reactions resolve in 5–15 minutes.",
            ]},
            { type: "h2", text: "After the storm" },
            { type: "ul", items: [
                "Don't reference what just happened — they may not remember and shame doesn't help.",
                "Note what triggered it: too many people, hunger, pain, fatigue, a complex question?",
                "Adjust the environment to prevent the next one.",
            ]},
            { type: "h2", text: "Patterns to break" },
            { type: "ul", items: [
                "Rushing them — 'come on, we have to go.'",
                "Choices with too many options — 'what do you want for breakfast?'",
                "Bright lights + loud TV + multiple voices at once.",
                "Care tasks in middle of sundowning hours.",
            ]},
        ],
        faq: [
            { q: "Is this aggression?", a: "It can look like aggression but the cause is different — it's overload, not anger directed at you. The same approaches help, but framing matters for your own resilience." },
            { q: "Do we need medication?", a: "For frequent or violent catastrophic reactions, a doctor may consider low-dose antipsychotics or antidepressants. Environmental changes work first for most families." },
        ],
        related: ["aggression", "agitation", "sundowning", "hallucinations"],
        lane_prompt: "Dad just exploded over nothing. What just happened?",
        updated_at: "2026-02-27",
    },

    // ===== PAYING FOR CARE =====
    {
        slug: "medicare-vs-medicaid",
        group: "paying-for-care",
        title: "Medicare vs Medicaid for Dementia Care — The Real Differences",
        meta: "Medicare and Medicaid sound alike but cover wildly different things in dementia care. A 5-minute decoder.",
        h1: "Medicare vs Medicaid",
        summary: "Medicare is age-based (65+) and covers acute care. Medicaid is income-based and covers long-term care. Most families need both before this is over.",
        body: [
            { type: "h2", text: "Quick decoder" },
            { type: "ul", items: [
                "Medicare = 65+ insurance, federal, covers hospital and short-term skilled care.",
                "Medicaid = means-tested insurance, state-run, covers long-term care (nursing home, in-home services).",
                "Medicare Advantage = a private alternative to Original Medicare with bundled benefits.",
                "Medicare GUIDE program = new (2024+) dementia-specific Medicare benefit. Navigator, helpline, $2,500/yr respite.",
            ]},
            { type: "h2", text: "What Medicare DOES cover for dementia" },
            { type: "ul", items: [
                "Diagnosis: doctor visits, brain MRI, blood work, neuropsych testing.",
                "Medications via Part D (Aricept, Namenda, Leqembi where eligible).",
                "Up to 100 days of skilled nursing facility care after a 3-day hospitalization.",
                "Home health (skilled, short-term — not long-term help with bathing).",
                "Hospice — when eligible. 100% covered.",
                "GUIDE program — 24/7 helpline, care navigator, up to $2,500/yr respite.",
            ]},
            { type: "h2", text: "What Medicare does NOT cover" },
            { type: "ul", items: [
                "Long-term nursing home (custodial care).",
                "Personal aides for bathing, dressing, meal prep.",
                "Adult day programs (except some via GUIDE respite).",
                "Memory-care assisted living.",
            ]},
            { type: "h2", text: "Where Medicaid comes in" },
            { type: "p", text: "Once savings are drawn down (varies by state, roughly $2,000–$3,000 in countable assets for a single person), Medicaid pays for nursing-home care and many states pay for in-home Home and Community Based Services (HCBS) via 1915(c) waivers. Look-back period is 5 years — start planning early." },
        ],
        faq: [
            { q: "Can someone have both Medicare and Medicaid?", a: "Yes — they're called 'dual eligibles' and qualify for some of the most generous benefits. Special Needs Plans (D-SNPs) coordinate both." },
            { q: "Will Medicaid take our house?", a: "Generally no while the spouse lives there. After both die, Medicaid Estate Recovery may claim against the estate. An elder-law attorney can structure assets to protect the home." },
        ],
        related: ["medicare-coverage", "medicaid-waivers", "guide-eligibility", "ltc-insurance", "cost-of-memory-care"],
        lane_prompt: "Does Medicare or Medicaid cover dementia care?",
        updated_at: "2026-02-27",
    },
    {
        slug: "cost-of-memory-care",
        group: "paying-for-care",
        title: "What Memory Care Actually Costs in 2026",
        meta: "Memory care in the US averages $7,000–$10,000/month in 2026. What drives the price, where to find cheaper, and how to pay.",
        h1: "Cost of memory care",
        summary: "Memory care is the most expensive long-term care setting short of a private-pay nursing home. National median is roughly $8,000/month in 2026.",
        body: [
            { type: "h2", text: "Typical 2026 prices" },
            { type: "ul", items: [
                "Memory-care assisted living (private studio): $7,000–$10,000/month national median.",
                "Premium urban markets (NY, Boston, SF): $9,000–$13,000/month.",
                "Lower-cost regions (Midwest, rural South): $5,500–$7,500/month.",
                "Nursing home (semi-private): $8,500/month median; private $10,000+/month.",
                "Adult day program: $80–$150/day, generally cheaper than in-home aides.",
            ]},
            { type: "h2", text: "What drives the price" },
            { type: "ul", items: [
                "Acuity level — heavier care needs cost more.",
                "Private vs shared room.",
                "Memory-care wing inside a larger community vs stand-alone secure community.",
                "Geographic market.",
                "Optional services (medication management, incontinence supplies, transport).",
            ]},
            { type: "h2", text: "How families pay" },
            { type: "ul", items: [
                "Private savings, retirement, social security. Most common, not sustainable.",
                "Long-term care insurance — only if purchased before diagnosis.",
                "Veterans Aid & Attendance — up to $2,727/month for surviving spouse, $2,895/month for a veteran (2026 rates).",
                "Selling the home or taking a reverse mortgage.",
                "Medicaid — only after spending down assets. Look-back is 5 years.",
            ]},
            { type: "callout", tone: "teal", text: "Memory care averages $96,000–$120,000/year. Most families exhaust private savings within 2–4 years. Plan early — Medicaid look-back means asset transfers must happen before 5 years out." },
        ],
        faq: [
            { q: "Why is memory care more expensive than regular assisted living?", a: "Higher staff ratios (often 1:6 instead of 1:15), 24/7 supervision, secure exits, specialized programming, and often higher real-estate costs for purpose-built communities." },
            { q: "Are there cheaper options that still work?", a: "Yes — smaller residential care homes (often 6 residents), in-home aides paired with adult day, or a hybrid of family caregiving + part-time aides. None is right for every family." },
        ],
        related: ["moving-to-memory-care", "medicaid-waivers", "selling-the-house-to-pay", "ltc-insurance", "va-aid-and-attendance"],
        lane_prompt: "How much does memory care actually cost?",
        updated_at: "2026-02-27",
    },
    {
        slug: "selling-the-house-to-pay",
        group: "paying-for-care",
        title: "Selling the Home to Pay for Care — Pros, Cons, and Alternatives",
        meta: "When does it make sense to sell the family home to fund dementia care, and what alternatives keep the door open?",
        h1: "Selling the home to pay for care",
        summary: "Selling is often necessary but rarely urgent. Before listing, look at reverse mortgages, sale-leasebacks, and Medicaid asset protection.",
        body: [
            { type: "h2", text: "Reasons it makes sense" },
            { type: "ul", items: [
                "Loved one has moved permanently to memory care; spouse has also moved out.",
                "House is the only large asset and care needs years of funding.",
                "Maintenance + property taxes are draining cash flow.",
                "Stairs and layout make returning home unsafe.",
            ]},
            { type: "h2", text: "Reasons to wait" },
            { type: "ul", items: [
                "Spouse still lives in the home — selling collapses their stability.",
                "Long-term care insurance is still active and may pay for care.",
                "Medicaid spend-down strategy benefits from KEEPING the house (it's exempt for the well spouse).",
                "Capital gains taxes will eat the proceeds — check with a CPA on step-up basis after death.",
            ]},
            { type: "h2", text: "Alternatives to outright sale" },
            { type: "ul", items: [
                "Reverse mortgage (HECM) — for the spouse still in the home age 62+.",
                "Home equity line of credit (HELOC) — short-term liquidity.",
                "Sale-leaseback companies — sell and rent it back at market rate.",
                "Family rental — one adult child buys out siblings and rents to parent.",
                "Title-in-trust transfers — only if done before the 5-year Medicaid look-back.",
            ]},
            { type: "callout", tone: "coral", text: "If Medicaid is in your future, asset transfers in the 5-year look-back create penalty periods. Talk to an elder-law attorney BEFORE selling, transferring, or gifting the house." },
        ],
        faq: [
            { q: "Does Medicaid take the house?", a: "While the spouse is alive and living there, no. After both die, Medicaid Estate Recovery may claim against the estate. An elder-law attorney can structure ownership to protect heirs." },
            { q: "How fast does memory care eat home equity?", a: "Roughly $96,000–$120,000/year for memory care. A $400,000 home equity buffer typically funds 3–4 years." },
        ],
        related: ["cost-of-memory-care", "medicaid-waivers", "ltc-insurance", "power-of-attorney"],
        lane_prompt: "Should we sell the family home to pay for memory care?",
        updated_at: "2026-02-27",
    },
    {
        slug: "caregiver-tax-deductions",
        group: "paying-for-care",
        title: "Caregiver Tax Deductions and Credits — What's Actually Available",
        meta: "From the Dependent Care Credit to medical expense deductions, what dementia caregivers can claim on US taxes.",
        h1: "Caregiver tax breaks",
        summary: "Caregiving is expensive. Several US tax provisions help offset the cost. Most caregivers don't claim them because no one tells them.",
        body: [
            { type: "h2", text: "The big ones" },
            { type: "ul", items: [
                "Medical expense deduction — care costs above 7.5% of AGI may be deductible. Includes memory care, adult day, in-home aides for ADLs.",
                "Dependent Care Credit — up to $3,000 for ONE qualifying person, $6,000 for two. Requires you (and spouse) be working or looking for work.",
                "Qualifying Relative — if you provide >50% of support and the person's gross income is under the IRS limit (about $5,050 in 2026), you can claim them as a dependent.",
                "FSA / HSA contributions — your employer's flexible spending account can pay qualifying caregiving expenses pre-tax.",
            ]},
            { type: "h2", text: "Often-missed deductions" },
            { type: "ul", items: [
                "Home modifications for accessibility (grab bars, ramps, walk-in shower) — deductible if not increasing home value.",
                "Mileage to/from medical appointments — $0.22/mile in 2026.",
                "Transportation services for the person you care for.",
                "Legal and accounting fees related to care management.",
                "Special equipment: hospital beds, wheelchairs, GPS trackers.",
            ]},
            { type: "callout", tone: "teal", text: "Document everything. A simple spreadsheet of date, expense, amount, purpose makes tax time survivable. Receipts in a labeled box are fine." },
        ],
        faq: [
            { q: "Can I deduct memory care monthly fees?", a: "Yes — the medical-care portion is deductible. The lodging portion may also be deductible if the primary reason for residence is medical care, which most memory-care placements are." },
            { q: "What about paid family caregivers?", a: "Several states (CA, NY, NJ, WA, others) have paid family caregiver programs through Medicaid or state funds. Check 'Cash and Counseling' / 'Consumer Directed Personal Assistance Program' in your state." },
        ],
        related: ["cost-of-memory-care", "medicaid-waivers", "ltc-insurance", "social-security-disability-dementia"],
        lane_prompt: "What tax deductions can I claim as a dementia caregiver?",
        updated_at: "2026-02-27",
    },
    {
        slug: "social-security-disability-dementia",
        group: "paying-for-care",
        title: "SSDI for Dementia — The Compassionate Allowances Process",
        meta: "Early-onset Alzheimer's and several other dementias qualify for SSDI fast-track approval. What to file, how long it takes.",
        h1: "SSDI for dementia",
        summary: "If your loved one was diagnosed before 65 and was still working, Social Security Disability Insurance (SSDI) can replace income within weeks, not years.",
        body: [
            { type: "h2", text: "Who qualifies" },
            { type: "ul", items: [
                "Diagnosed with Alzheimer's, early-onset Alzheimer's, frontotemporal dementia, Lewy body dementia, or vascular dementia.",
                "Has enough work credits — roughly 40 quarters of work over a lifetime.",
                "Disability is expected to last at least 12 months (dementia diagnosis qualifies).",
            ]},
            { type: "h2", text: "What's the Compassionate Allowances list?" },
            { type: "p", text: "SSA's Compassionate Allowances (CAL) program fast-tracks disability decisions for conditions that obviously qualify. Early-onset Alzheimer's and frontotemporal dementia are both on it. Approval often comes in 2–6 weeks vs the usual 6–18 months." },
            { type: "h2", text: "What to file" },
            { type: "ol", items: [
                "SSA-16 (disability application) or apply online at ssa.gov.",
                "SSA-3373 (function report) — describing daily limitations.",
                "Medical records from the diagnosing neurologist or memory clinic.",
                "Cognitive testing results (MMSE, MoCA, neuropsych battery).",
                "Statements from family on observed decline.",
            ]},
            { type: "h2", text: "After approval" },
            { type: "ul", items: [
                "Cash benefits start after a 5-month waiting period.",
                "Medicare eligibility starts 24 months after SSDI begins (Compassionate Allowances does NOT waive this — but a bill in Congress aims to change it).",
                "Family members may also be eligible (auxiliary benefits for spouse, children).",
            ]},
        ],
        faq: [
            { q: "What if they're over 65 already?", a: "Then SSDI doesn't apply (Social Security retirement does instead). But other supports — VA Aid & Attendance, Medicaid waivers, GUIDE — still help." },
            { q: "Does an SSDI approval affect Medicaid?", a: "It can — SSDI is counted income. An elder-law attorney can structure things to preserve Medicaid eligibility (Pooled trusts, ABLE accounts in some cases)." },
        ],
        related: ["early-onset-alzheimers", "medicaid-waivers", "ltc-insurance", "va-aid-and-attendance"],
        lane_prompt: "Can we get SSDI for my husband's early-onset Alzheimer's?",
        updated_at: "2026-02-27",
    },

    // ===== GUIDE =====
    {
        slug: "guide-after-eligibility",
        group: "guide",
        title: "You're In the GUIDE Program — What Happens Next",
        meta: "Once you've qualified for the Medicare GUIDE dementia program, here's the actual playbook for the first 90 days.",
        h1: "After you're enrolled in GUIDE",
        summary: "GUIDE participants get a Care Navigator, 24/7 helpline, and up to $2,500/year of respite. Knowing what to ask for matters as much as having access.",
        body: [
            { type: "h2", text: "Your first 30 days" },
            { type: "ol", items: [
                "Care Navigator assigned — meet by phone or video within 14 days.",
                "Initial comprehensive assessment — they cover the caregiver, not just the patient.",
                "Care plan drafted with you. Don't accept boilerplate. Push for specifics on your top 3 worries.",
                "24/7 helpline number programmed in your phone. Test-call it during business hours so you know what it sounds like.",
            ]},
            { type: "h2", text: "Months 1–3 — what to use it for" },
            { type: "ul", items: [
                "Respite. Up to $2,500/year. Start scheduling NOW — adult day, weekend home aide, even a hotel weekend covered if the GUIDE practice approves.",
                "Care plan calls every 30 days. Use them. Most caregivers underuse navigator access.",
                "Behavior crises — call the 24/7 line BEFORE the ER. Many crises de-escalate over the phone.",
                "Provider coordination — let the navigator be the one repeating context to specialists.",
            ]},
            { type: "h2", text: "What GUIDE does NOT cover" },
            { type: "ul", items: [
                "Long-term care costs (memory care, nursing home).",
                "Personal-care aides for daily ADLs.",
                "Out-of-network specialists if the GUIDE practice isn't part of your loved one's care.",
            ]},
            { type: "callout", tone: "teal", text: "GUIDE benefits don't auto-renew if you don't use them. Spend the respite dollars — they're meant to keep you in the role longer." },
        ],
        faq: [
            { q: "Can I switch GUIDE practices?", a: "Yes, like any Medicare provider. Talk to your navigator first — sometimes the issue is fixable." },
            { q: "Is everything from GUIDE truly free?", a: "Care navigation and helpline: yes, no copay. Respite: free up to $2,500/year. Medical visits within GUIDE still follow normal Medicare cost-sharing." },
        ],
        related: ["guide-eligibility", "guide-faq", "finding-respite", "pace"],
        lane_prompt: "We just enrolled in GUIDE. What do I do now?",
        updated_at: "2026-02-27",
    },
    {
        slug: "respite-vs-adult-day",
        group: "guide",
        title: "Respite vs Adult Day Programs — Which Helps More?",
        meta: "Respite, adult day, and home aides all give caregivers a break. Here's what each does best and how much they cost.",
        h1: "Respite vs adult day",
        summary: "Both buy caregivers time. They work differently — and you may need both at different stages.",
        body: [
            { type: "h2", text: "Quick comparison" },
            { type: "ul", items: [
                "Respite = scheduled time off — could be in-home, weekend, or short stay at a facility. $20–$40/hr in-home; $200–$400/day inpatient.",
                "Adult Day Program = structured daytime program (5–8 hours), with meals + activities + nursing oversight. $80–$150/day.",
                "Home aide = a person comes to your house for ADL support, companionship, light housekeeping. $25–$40/hour.",
                "GUIDE respite benefit = up to $2,500/year reimbursable through Medicare GUIDE program.",
            ]},
            { type: "h2", text: "When adult day shines" },
            { type: "ul", items: [
                "Your loved one is still social and bored at home.",
                "You're still working — adult day fills your workday.",
                "Behavior issues like sundowning, shadowing — structured day with peers helps both.",
                "You want predictability — same days, same hours, same vans home.",
            ]},
            { type: "h2", text: "When in-home respite shines" },
            { type: "ul", items: [
                "Your loved one is mid-late stage and travel is too disruptive.",
                "You need flexibility — overnight, weekend, or evening only.",
                "Behavior risks make group settings harder.",
                "You need a 1-on-1 relationship for trust to build.",
            ]},
            { type: "callout", tone: "teal", text: "Adult day is the cheapest hourly respite per dollar. If your loved one tolerates it, start there — it often opens up entire workdays without breaking your budget." },
        ],
        faq: [
            { q: "Will Medicare pay for adult day?", a: "Original Medicare: no. Medicare GUIDE respite dollars (up to $2,500/year): yes. Many Medicaid HCBS waivers: yes. Some Medicare Advantage plans now include adult day." },
            { q: "How do I find a good adult day program?", a: "Ask your GUIDE navigator. Failing that, the local Area Agency on Aging and the Alzheimer's Association both keep up-to-date directories." },
        ],
        related: ["finding-respite", "guide-eligibility", "guide-after-eligibility", "caregiver-burnout-signs"],
        lane_prompt: "Should we try adult day or in-home respite?",
        updated_at: "2026-02-27",
    },
    {
        slug: "snf-vs-memory-care",
        group: "guide",
        title: "Skilled Nursing vs Memory Care — Which One Does Your Loved One Need?",
        meta: "SNF, memory care, assisted living, and CCRCs all sound similar. Here's a plain-English decoder of who needs which.",
        h1: "Skilled nursing vs memory care",
        summary: "Memory care is for cognitive needs. Skilled nursing is for medical needs. Many late-stage dementia patients eventually need both.",
        body: [
            { type: "h2", text: "Quick comparison" },
            { type: "ul", items: [
                "Assisted living = housing + ADL help. No locked doors. Best for early stage.",
                "Memory care = secure assisted living with dementia-trained staff and dementia-specific programming. Mid-stage and beyond.",
                "Skilled nursing facility (SNF) = nursing-level medical care. Late-stage dementia + complex medical needs (IV, wound care, ventilator).",
                "CCRC (continuing care community) = all three on one campus with progression built in.",
            ]},
            { type: "h2", text: "When to consider memory care" },
            { type: "ul", items: [
                "Wandering or exit-seeking creates safety risk.",
                "Behaviors (sundowning, paranoia, hallucinations) are overwhelming for home caregivers.",
                "Sleeps poorly, needs constant supervision overnight.",
                "Family caregiver is burning out and respite isn't enough.",
            ]},
            { type: "h2", text: "When to consider skilled nursing" },
            { type: "ul", items: [
                "Multiple ADL dependence + complex medical conditions (heart failure, dialysis, recent stroke).",
                "Recurrent hospitalizations.",
                "Tube feeding (rare in dementia — see hospice conversation).",
                "Pressure injuries needing wound care.",
            ]},
            { type: "callout", tone: "coral", text: "Many dementia patients eventually transition from memory care to SNF in their last months. Hospice is also an option at this stage and often the better one — covered 100% by Medicare." },
        ],
        faq: [
            { q: "Can someone in memory care use hospice?", a: "Yes — hospice can come to memory care, SNF, or home. It's a benefit, not a place." },
            { q: "Will Medicare cover memory care?", a: "Generally no for the room-and-board portion. Some medical services delivered inside memory care may be covered. Medicaid will once spend-down is complete." },
        ],
        related: ["moving-to-memory-care", "hospice-benefit", "medicaid-waivers", "cost-of-memory-care"],
        lane_prompt: "Does dad need memory care or skilled nursing?",
        updated_at: "2026-02-27",
    },

    // ===== HARD CONVERSATIONS =====
    {
        slug: "telling-loved-one-they-cant-drive",
        group: "hard-conversations",
        title: "How to Tell Mom or Dad They Can't Drive Anymore",
        meta: "The driving conversation is one of the hardest in dementia. A script that protects safety AND dignity.",
        h1: "Telling them they can't drive",
        summary: "It's not about license. It's about identity, independence, and the last thing they can still do alone. Plan the conversation; don't ambush it.",
        body: [
            { type: "h2", text: "First — make sure you're right" },
            { type: "ul", items: [
                "Ride along three times in different conditions.",
                "Note hesitations, lane drifts, stop-sign rolls, missed turns.",
                "Compare to a baseline you'd accept from any driver.",
                "Ask the doctor to order an Occupational Therapy driving evaluation — neutral 3rd-party assessment.",
            ]},
            { type: "h2", text: "The conversation" },
            { type: "ol", items: [
                "Pick a calm, low-stimulus time. Not on the way somewhere, not after a fall.",
                "Lead with love: 'Dad, I'd never forgive myself if something happened.'",
                "Anchor it externally: 'The doctor recommended you stop driving.' Even better: written letter from the doctor.",
                "Acknowledge the loss: 'I know this is awful. Driving has always been freedom for you.'",
                "Offer alternatives: ride-share, family schedule, senior van services, GUIDE-covered transport.",
            ]},
            { type: "h2", text: "When the conversation doesn't work" },
            { type: "ul", items: [
                "Disable the car quietly — battery cable, hidden ignition switch, 'mechanic says it's in the shop.'",
                "Sell the car and reframe: 'Insurance got too expensive, so we're sharing rides.'",
                "Many states allow physicians (and sometimes family) to report unsafe drivers — DMV revokes the license.",
            ]},
            { type: "callout", tone: "teal", text: "There is no perfect way to do this. Many caregivers say this conversation is harder than the diagnosis itself. You're not breaking the relationship by protecting your loved one." },
        ],
        faq: [
            { q: "Should I just take the keys?", a: "Not as a first move — it usually escalates conflict. Try the conversation, then the doctor's letter, then the disabled car. Taking keys outright is a last resort that often backfires." },
            { q: "What if they keep trying to drive?", a: "Multiple losses of keys + a 'mechanic delay' usually buys weeks. Eventually selling the car is the cleanest. A few states will allow remote ignition lockouts for medical reasons." },
        ],
        related: ["stop-driving", "when-to-take-the-keys", "sharing-the-diagnosis", "talking-with-siblings"],
        lane_prompt: "How do I tell my dad he can't drive anymore?",
        updated_at: "2026-02-27",
    },
    {
        slug: "talking-to-kids-about-dementia",
        group: "hard-conversations",
        title: "Talking to Kids About Grandma's Dementia — By Age",
        meta: "What to say to kids 4, 8, 12, and 16 about a grandparent's dementia. Age-appropriate scripts and books.",
        h1: "Telling kids about dementia",
        summary: "Kids notice everything. Naming what's happening — at their level — makes them less scared, not more.",
        body: [
            { type: "h2", text: "Ages 3–6" },
            { type: "ul", items: [
                "Keep it simple: 'Grandma's brain is getting tired. Sometimes she forgets things or gets confused. It's nobody's fault.'",
                "Reassure: 'She still loves you, even when she can't remember your name.'",
                "Book: 'What Happened to Grandpa?' by Maria Shriver.",
            ]},
            { type: "h2", text: "Ages 7–11" },
            { type: "ul", items: [
                "Name the disease: 'Grandma has Alzheimer's. It's a disease that hurts the brain.'",
                "Explain what they might see — repeating questions, getting lost, mood changes.",
                "Empower: 'Things YOU can do that help — show her photos, sing with her, hold her hand.'",
                "Book: 'Still My Grandma' by Veronique Van den Abeele.",
            ]},
            { type: "h2", text: "Ages 12–17" },
            { type: "ul", items: [
                "Be honest about progression. They can handle 'she will get worse.'",
                "Talk about what NOT to share on social media — privacy and dignity.",
                "Acknowledge their grief — they're losing the grandparent they knew.",
                "Offer space — they shouldn't be a primary caregiver, but small involvement is healing.",
                "Watch for behavior changes (school slipping, withdrawal) — therapy referral if needed.",
            ]},
            { type: "callout", tone: "teal", text: "Don't make kids responsible for protecting adults from emotion. They will see you cry. Naming it is fine: 'I'm sad today, and that's okay. I love grandma.'" },
        ],
        faq: [
            { q: "Should young kids visit a grandparent with late-stage dementia?", a: "Usually yes, in short focused visits. Even when recognition is gone, the warmth often comes through. Prep kids on what to expect each visit." },
            { q: "What about kids who are scared?", a: "Don't force visits. Let them participate by drawing pictures, sending recordings, helping you with something tangible." },
        ],
        related: ["sharing-the-diagnosis", "talking-with-siblings", "anticipatory-grief", "caregiver-burnout-signs"],
        lane_prompt: "How do I tell my kids about grandma's dementia?",
        updated_at: "2026-02-27",
    },
    {
        slug: "when-to-stop-treatment",
        group: "hard-conversations",
        title: "When to Stop Treatment — A Family Conversation Guide",
        meta: "The line between treating disease and prolonging suffering. How to talk with family and doctors about pulling back.",
        h1: "When to stop treatment",
        summary: "There is a point in advanced dementia when treatments stop helping and start hurting. Naming that moment is one of the most loving things a family can do.",
        body: [
            { type: "h2", text: "Signals it may be time" },
            { type: "ul", items: [
                "Multiple hospitalizations in 6 months.",
                "Stopped eating or drinking meaningfully despite multiple approaches.",
                "Loss of recognition, language, ability to walk.",
                "Recurrent infections (UTIs, pneumonia) — body's defenses are exhausted.",
                "Comfort is suffering — every transfer hurts, every test causes distress.",
            ]},
            { type: "h2", text: "What 'stopping treatment' usually looks like" },
            { type: "ul", items: [
                "Hospice enrollment — covered 100% by Medicare. Doesn't mean 'giving up.' Means shifting goals to comfort.",
                "DNR / DNI order — no CPR, no ventilator.",
                "Hospital transfer pause — most issues addressable at home with hospice nursing.",
                "Stopping disease-modifying meds (donepezil, memantine) — they often weren't doing much by this point.",
                "Continuing comfort meds — pain, anxiety, breathing — these stay.",
            ]},
            { type: "h2", text: "Family conversations" },
            { type: "ul", items: [
                "Use a 'best case / worst case' framework — what would they want if they could choose today?",
                "Refer back to their written wishes (POLST, living will, healthcare proxy).",
                "Include all decision-makers — surprises after death cause years of grief.",
                "Ask: 'What would dad have wanted at this moment?' not 'What do you want for dad?'",
            ]},
            { type: "callout", tone: "coral", text: "Hospice does not hasten death. National data shows people on hospice often live LONGER than people pursuing aggressive treatment at end of life, with better comfort." },
        ],
        faq: [
            { q: "Will the doctor bring this up?", a: "Often no — many primary docs avoid these conversations. You may need to ask: 'Are we still helping, or are we just causing distress?'" },
            { q: "Can we still treat treatable things on hospice?", a: "Yes — UTIs causing pain, falls, comfort medications all stay. Hospice doesn't mean withholding everything; it means the goal is comfort." },
        ],
        related: ["end-of-life-wishes", "hospice-benefit", "anticipatory-grief", "talking-with-siblings"],
        lane_prompt: "Is it time to stop treatment for mom?",
        updated_at: "2026-02-27",
    },

    // ===== RESOURCES =====
    {
        slug: "emergency-room-with-dementia",
        group: "resources",
        title: "Taking Someone With Dementia to the ER — A Survival Guide",
        meta: "ER visits with dementia patients are chaotic and traumatic. The prep, the ask, the bag, and what to do if you must.",
        h1: "ER visits with dementia",
        summary: "ERs are loud, bright, slow, and full of strangers — exactly the wrong environment for a confused brain. Prep makes the difference.",
        body: [
            { type: "h2", text: "Before you go (when you can) " },
            { type: "ol", items: [
                "Call the GUIDE 24/7 line or primary care first — many crises de-escalate over the phone.",
                "Print/screenshot the Continuity Passport (or a one-page summary).",
                "Pack a 'hospital bag' — change of clothes, hearing aids, glasses, dentures, comfort object, favorite snack, list of medications.",
                "Bring a healthcare proxy / POA copy — most ERs will demand it before letting you make decisions.",
            ]},
            { type: "h2", text: "At the ER" },
            { type: "ul", items: [
                "Speak up at triage: 'This person has dementia. They cannot wait alone. They will become agitated.'",
                "Ask for a quiet room when possible.",
                "Stay with them. Don't leave to get coffee unless someone else can sit.",
                "Limit IVs, restraints, and bright lights — request these only when necessary.",
                "Sundowning will hit during ER visits. Bring food, water, hydration. Avoid sedating meds if possible.",
            ]},
            { type: "h2", text: "Things to insist on" },
            { type: "ul", items: [
                "Quick UTI screen if confusion has worsened (urine dip + culture).",
                "Pain assessment — undiagnosed pain is the #1 missed cause of dementia ER visits.",
                "Med list review for anticholinergics, benzos, opioids.",
                "Discharge plan that includes the GUIDE navigator's name and number.",
            ]},
            { type: "callout", tone: "teal", text: "When you can avoid the ER, do. Telehealth, urgent care, GUIDE 24/7 line, and hospice all reduce ER visits — which often cause more decline than they fix." },
        ],
        faq: [
            { q: "Should I go to the ER for a UTI?", a: "Only if there's fever, severe lethargy, or signs of sepsis. Most UTIs can be diagnosed and treated at primary care or urgent care." },
            { q: "What about hospital-acquired delirium?", a: "Real and common. A 3-day hospital stay can drop cognition by months. Minimize stays when possible." },
        ],
        related: ["telehealth-options", "dementia-and-uti", "agitation", "hospice-benefit"],
        lane_prompt: "We need to take mom to the ER. How do I prepare?",
        updated_at: "2026-02-27",
    },
    {
        slug: "dementia-friendly-businesses",
        group: "resources",
        title: "Dementia-Friendly Businesses — How to Find Them",
        meta: "From restaurants to barbers to banks, more places are training staff to support dementia patients. Here's how to find them in your area.",
        h1: "Finding dementia-friendly businesses",
        summary: "Dementia-friendly communities are growing fast. Knowing how to find — and ask for — them keeps your loved one engaged in the world longer.",
        body: [
            { type: "h2", text: "What 'dementia-friendly' actually means" },
            { type: "ul", items: [
                "Staff trained in dementia communication (slower, eye contact, no rushed transactions).",
                "Environment that reduces overwhelm — quieter, simpler menus, fewer choices on display.",
                "Allowances for repetition, slow payment, occasional confusion.",
                "Often certified by Dementia Friendly America or a local Alzheimer's chapter.",
            ]},
            { type: "h2", text: "How to find them" },
            { type: "ul", items: [
                "Dementia Friendly America — dfamerica.org has a community list.",
                "Local Alzheimer's Association chapter — keeps a directory.",
                "Memory Lane resource finder — filter by dementia-friendly tag.",
                "Just ask — many local businesses have informal training and will say yes if you call ahead.",
            ]},
            { type: "h2", text: "Best businesses to look for" },
            { type: "ul", items: [
                "Hair salons / barbers with quiet morning hours.",
                "Restaurants with senior matinee menus, simple choices, calm lighting.",
                "Banks with a 'preferred banker' you can ask for by name.",
                "Pharmacies that do delivery and medication synchronization.",
                "Faith communities with dementia-friendly worship services.",
            ]},
            { type: "callout", tone: "teal", text: "If you find a business that handles your loved one well, write a thank-you note and tell others. These places exist because someone notices and rewards them." },
        ],
        faq: [
            { q: "Can we ask businesses to train staff?", a: "Yes — Dementia Friends offers free 1-hour sessions for any business. A friendly conversation with a manager often goes a long way." },
            { q: "What if a business handles us badly?", a: "Most of the time, it's lack of awareness, not malice. A quick polite note often opens conversations. Memory Lane's resource directory lets you flag businesses too." },
        ],
        related: ["provider-search", "choosing-adult-day-program", "telehealth-options"],
        lane_prompt: "How do I find dementia-friendly businesses near me?",
        updated_at: "2026-02-27",
    },
    {
        slug: "legal-documents-checklist",
        group: "resources",
        title: "Legal Documents Every Dementia Family Needs — A Checklist",
        meta: "Power of attorney, healthcare proxy, advance directives, will, trust. Which to get, when, and what they each do.",
        h1: "Legal documents checklist",
        summary: "Five core documents protect your loved one (and your sanity). Get them while capacity is intact — once it's gone, the courts make decisions instead of you.",
        body: [
            { type: "h2", text: "The five essential documents" },
            { type: "ol", items: [
                "Durable Financial Power of Attorney — appoints someone to manage money, bills, taxes if your loved one can't.",
                "Durable Healthcare Power of Attorney (Healthcare Proxy) — appoints someone to make medical decisions.",
                "Living Will / Advance Directive — written wishes about life-sustaining treatment.",
                "POLST or MOLST (state-specific) — medical orders for end-of-life care signed by a doctor. Travels with the patient.",
                "Last Will and Testament — distributes assets after death. A revocable trust often complements or replaces this.",
            ]},
            { type: "h2", text: "When to get them" },
            { type: "ul", items: [
                "BEFORE diagnosis if possible.",
                "Within 90 days of diagnosis if capacity is still mostly intact.",
                "Many people with mild cognitive impairment or early dementia still have capacity to sign — talk to the attorney about a capacity letter from the doctor.",
                "After capacity is gone, only court-appointed guardianship/conservatorship works — slow, expensive, and adversarial.",
            ]},
            { type: "h2", text: "Who creates them" },
            { type: "ul", items: [
                "Elder-law attorney — best, ~$2,000–$5,000 for a full set. NAELA (National Academy of Elder Law Attorneys) has a finder.",
                "Online forms (LegalZoom, Trust & Will) — cheaper but less robust. Fine for simple situations.",
                "State bar association free / low-cost referral programs.",
                "VA caregiver legal support if your loved one is a veteran.",
            ]},
            { type: "callout", tone: "coral", text: "Without a POA, family members often spend $10,000–$30,000 in court for emergency guardianship. Spend $3,000 with an attorney now — save the heartbreak and the money later." },
        ],
        faq: [
            { q: "Can we draft them ourselves?", a: "Yes, but state requirements vary on witnesses, notaries, and language. A small mistake invalidates the document. An attorney is worth it for any family with assets, real estate, or complex relationships." },
            { q: "What about HIPAA release?", a: "Add one. It allows providers to share medical info with the people on the list. Hospitals will refuse to talk without one — even to a healthcare proxy in some cases." },
        ],
        related: ["power-of-attorney", "end-of-life-wishes", "talking-with-siblings", "selling-the-house-to-pay"],
        lane_prompt: "What legal documents do we need before mom gets worse?",
        updated_at: "2026-02-27",
    },
];
